'use client';

import React, { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { loadTestimonials, type TestimonialCollection } from '@/lib/content';
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import BackgroundDecor from '../BackgroundDecor';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState<TestimonialCollection>({ items: [] });
  const { t } = useTranslations();

  useEffect(() => {
    loadTestimonials().then(setTestimonials);
  }, []);

  return (
    <section className="py-20 relative">
      <BackgroundDecor section="testimonials" seed={101} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {t("testimonials.title", "What Our")} {" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              {t("testimonials.titleHighlight", "Customers Say")}
            </span>
          </h2>
          <p className="text-xl text-muted-foreground">
            {t("testimonials.subtitle", "Join thousands of travelers who stay connected with eSIMSupra")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.items.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="glass rounded-xl p-6 hover:shadow-glow transition-smooth animate-fade-in"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-warning text-warning"
                    />
                  ))}
                </div>
                <Quote className="w-6 h-6 text-primary/50" />
              </div>

              <blockquote className="text-sm text-muted-foreground mb-6 leading-relaxed">
                "{testimonial.quote}"
              </blockquote>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-primary flex items-center justify-center text-white font-semibold text-sm">
                  {testimonial.src ? (
                    <ExportedImage
                      src={testimonial.src}
                      alt={testimonial.author}
                      width={40}
                      height={40}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    testimonial.author
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                  )}
                </div>
                <div>
                  <div className="font-semibold text-foreground text-sm">
                    {testimonial.author}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {testimonial.location}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 glass rounded-full px-6 py-3">
            <span className="text-muted-foreground">{t("testimonials.ratingCaption", "Reviews from verified travelers")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}